// ─────────────────────────────────────────────────────────────────────────
// FinanceTimesheetAnalysis — proves the finance figure ties back to the
// approved timesheet: derived summary tiles, hours × rate equation, a
// read-only calendar heatmap (CalendarCard controlled mode), weekly rollup
// and the daily breakdown table. Everything is derived client-side from
// the record's dailyLogs — no extra API calls.
// ─────────────────────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { Table, Tooltip } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FieldTimeOutlined,
  RestOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import CalendarCard from '../cards/CalendarCard.jsx';
import { buildDayGradient } from '../timesheet/TimesheetComponents.jsx';
import {
  FinanceEquation,
  FinanceKpiRow,
  FinanceSection,
  FinanceStatusPill,
} from './FinanceComponents.jsx';
import {
  buildDailyRows,
  buildWeeklyRollup,
  deriveDayStats,
  formatHours,
  formatMoney,
  formatRate,
} from './financeUtils';
import './finance-components.css';

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

// ── Calendar day cell (read-only heatmap) ──────────────────────────────────

export function FinanceDayCell(dateKey, dayObj, { isToday } = {}) {
  const dayNumber = Number(dateKey.slice(-2));
  const st = num(dayObj?.workingHours?.ST);
  const ot = num(dayObj?.workingHours?.OT);
  const leave = num(dayObj?.leave?.CL) + num(dayObj?.leave?.SL);
  const total = st + ot;
  const logged = Boolean(dayObj?.isLogged) || total > 0 || leave > 0;

  const kind = !dayObj
    ? 'blank'
    : dayObj.isHoliday
      ? 'holiday'
      : logged && total > 0
        ? 'work'
        : leave > 0
          ? 'leave'
          : dayObj.isWeekOff
            ? 'weekoff'
            : 'blank';

  const cls = [
    'fin-daycell',
    `fin-daycell--${kind}`,
    dayObj?.status === 'approved' ? 'fin-daycell--approved' : '',
    dayObj?.status === 'rejected' ? 'fin-daycell--rejected' : '',
    isToday ? 'fin-daycell--today' : '',
  ].filter(Boolean).join(' ');

  const gradient = kind === 'work' ? buildDayGradient(dayObj) : null;

  const cell = (
    <div className={cls} style={gradient ? { background: gradient } : undefined}>
      <span className="fin-daycell__num">{dayNumber}</span>
      {kind === 'work' ? (
        <span className="fin-daycell__hours">
          {total % 1 === 0 ? total : total.toFixed(1)}h{ot > 0 ? <em> +OT</em> : null}
        </span>
      ) : kind === 'leave' ? (
        <span className="fin-daycell__hours">Leave</span>
      ) : kind === 'holiday' ? (
        <span className="fin-daycell__hours">Holiday</span>
      ) : null}
    </div>
  );

  if (!dayObj) return cell;
  const tooltip = [
    dayjs(dateKey).format('ddd, MMM D'),
    st ? `ST ${st}h` : null,
    ot ? `OT ${ot}h` : null,
    leave ? `Leave ${leave}h` : null,
    dayObj.isHoliday ? 'Holiday' : null,
    dayObj.status === 'rejected' ? `Rejected${dayObj.rejectionReason ? ` — ${dayObj.rejectionReason}` : ''}` : null,
    dayObj.status === 'approved' ? 'Approved' : null,
  ].filter(Boolean).join(' · ');
  return <Tooltip title={tooltip}>{cell}</Tooltip>;
}

const HEATMAP_LEGEND = [
  { label: 'Standard', color: '#3b82f6' },
  { label: 'Overtime', color: '#22c55e' },
  { label: 'Leave', color: '#f59e0b' },
  { label: 'Holiday', color: '#8b5cf6' },
  { label: 'Rejected', color: '#ef4444' },
];

// ── Main component ─────────────────────────────────────────────────────────

export default function FinanceTimesheetAnalysis({
  dailyLogs = [],
  timesheetSummary = {},
  rate = 0,
  rateLabel = 'Rate',
  amountLabel = 'Amount',
  approvedHours,
  month,
  year,
  monthLabel,
  extraEquationTerms = [],
}) {
  const stats = useMemo(() => deriveDayStats(dailyLogs), [dailyLogs]);
  const dayRows = useMemo(() => buildDailyRows(dailyLogs, rate), [dailyLogs, rate]);
  const weekRows = useMemo(() => buildWeeklyRollup(dailyLogs, rate), [dailyLogs, rate]);

  const totalHours = stats.totalHours || num(approvedHours);
  const totalAmount = totalHours * num(rate);
  const maxDayHours = Math.max(1, ...dayRows.map((d) => d.total));
  const maxWeekHours = Math.max(1, ...weekRows.map((w) => w.total));

  const tiles = [
    { key: 'hours', label: 'Approved Hours', value: formatHours(totalHours), icon: <ClockCircleOutlined />, tone: 'brand', sub: `${stats.loggedDays} logged days` },
    { key: 'st', label: 'Standard', value: formatHours(stats.stHours || timesheetSummary.totalSTHours), icon: <FieldTimeOutlined />, tone: 'neutral' },
    { key: 'ot', label: 'Overtime', value: formatHours(stats.otHours || timesheetSummary.totalOTHours), icon: <ThunderboltOutlined />, tone: 'warning' },
    { key: 'leave', label: 'Leave', value: formatHours(stats.leaveHours || timesheetSummary.leaveHours), icon: <RestOutlined />, tone: 'neutral', sub: `${stats.leaveDays} days` },
    { key: 'avg', label: 'Avg / Logged Day', value: formatHours(stats.avgHoursPerLoggedDay), icon: <CalendarOutlined />, tone: 'info' },
    { key: 'amount', label: amountLabel, value: formatMoney(totalAmount), icon: <DollarOutlined />, tone: 'success' },
  ];

  const dayChips = [
    { label: 'Approved days', value: stats.approvedDays, tone: 'success' },
    { label: 'Pending days', value: stats.pendingDays, tone: 'gold' },
    { label: 'Rejected days', value: stats.rejectedDays, tone: stats.rejectedDays ? 'danger' : 'neutral' },
    { label: 'Holidays', value: stats.holidayDays, tone: 'neutral' },
    { label: 'Week-offs', value: stats.weekOffDays, tone: 'neutral' },
  ];

  const dailyColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      width: 150,
      render: (d, row) => (
        <div className="fin-cell-text">
          <span className="fin-cell__primary">{d ? dayjs(d).format('ddd, MMM D') : '—'}</span>
          {row.isHoliday ? <span className="fin-cell__secondary">Holiday</span> : null}
        </div>
      ),
    },
    { title: 'ST', dataIndex: 'st', align: 'right', width: 70, render: (v) => v.toFixed(2) },
    { title: 'OT', dataIndex: 'ot', align: 'right', width: 70, render: (v) => (v ? v.toFixed(2) : '—') },
    { title: 'Leave', dataIndex: 'leave', align: 'right', width: 70, render: (v) => (v ? v.toFixed(2) : '—') },
    {
      title: 'Total',
      dataIndex: 'total',
      align: 'right',
      width: 170,
      render: (v) => (
        <div className="fin-bar">
          <span className="fin-bar__track"><span className="fin-bar__fill" style={{ width: `${(v / maxDayHours) * 100}%` }} /></span>
          <span className="fin-bar__value">{v.toFixed(2)}</span>
        </div>
      ),
    },
    { title: amountLabel, dataIndex: 'amount', align: 'right', width: 110, render: (v) => <strong className="fin-cell-money__value">{formatMoney(v)}</strong> },
    { title: 'Status', dataIndex: 'status', align: 'center', width: 100, render: (s) => <FinanceStatusPill status={s} size="sm" /> },
    { title: 'Reviewer', dataIndex: 'reviewer', width: 130, render: (v) => v || <span className="fin-cell__secondary">—</span> },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      ellipsis: true,
      render: (v) => (v ? <Tooltip title={v}><span className="fin-remarks">{v}</span></Tooltip> : <span className="fin-cell__secondary">—</span>),
    },
  ];

  const weeklyColumns = [
    { title: 'Week', dataIndex: 'label', render: (v, w) => <FinanceTextRow primary={v} secondary={`${w.days} logged days`} /> },
    { title: 'ST', dataIndex: 'st', align: 'right', width: 64, render: (v) => v.toFixed(1) },
    { title: 'OT', dataIndex: 'ot', align: 'right', width: 64, render: (v) => (v ? v.toFixed(1) : '—') },
    {
      title: 'Hours',
      dataIndex: 'total',
      align: 'right',
      width: 150,
      render: (v) => (
        <div className="fin-bar">
          <span className="fin-bar__track"><span className="fin-bar__fill" style={{ width: `${(v / maxWeekHours) * 100}%` }} /></span>
          <span className="fin-bar__value">{v.toFixed(1)}</span>
        </div>
      ),
    },
    { title: amountLabel, dataIndex: 'amount', align: 'right', width: 110, render: (v) => <strong className="fin-cell-money__value">{formatMoney(v)}</strong> },
  ];

  return (
    <div className="fin-analysis">
      <FinanceKpiRow items={tiles} compact />

      <div className="fin-daychips">
        {dayChips.map((chip) => (
          <span className={`fin-badge fin-badge--${chip.tone}`} key={chip.label}>
            {chip.value} {chip.label}
          </span>
        ))}
      </div>

      <FinanceSection className="fin-equation-card">
        <FinanceEquation
          terms={[
            { value: formatHours(totalHours), label: 'Approved Hours' },
            { op: '×' },
            { value: formatRate(rate), label: rateLabel },
            { op: '=' },
            { value: formatMoney(totalAmount), label: amountLabel, tone: 'accent' },
            ...extraEquationTerms,
          ]}
        />
      </FinanceSection>

      <div className="fin-analysis__grid">
        <CalendarCard
          className="fin-heatmap"
          days={dailyLogs}
          startDay="Mon"
          readonly
          year={year}
          month={month}
          monthLabel={monthLabel}
          legend={HEATMAP_LEGEND}
          renderDayContent={FinanceDayCell}
          onPrevMonth={() => {}}
          onNextMonth={() => {}}
        />
        <FinanceSection title="Weekly Breakdown" className="fin-weekly">
          <Table
            className="fin-analysis-table"
            size="small"
            rowKey={(w) => w.label}
            pagination={false}
            dataSource={weekRows}
            columns={weeklyColumns}
            locale={{ emptyText: 'No logged weeks' }}
          />
        </FinanceSection>
      </div>

      <FinanceSection title={`Daily Breakdown — Hours × ${rateLabel} = ${amountLabel}`}>
        <Table
          className="fin-analysis-table"
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={dayRows}
          columns={dailyColumns}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No approved day logs to analyse' }}
          summary={() =>
            dayRows.length ? (
              <Table.Summary.Row className="fin-analysis-total">
                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{stats.stHours.toFixed(2)}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">{stats.otHours ? stats.otHours.toFixed(2) : '—'}</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{stats.leaveHours ? stats.leaveHours.toFixed(2) : '—'}</Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><strong>{totalHours.toFixed(2)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right"><strong>{formatMoney(totalAmount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={6} />
                <Table.Summary.Cell index={7} />
                <Table.Summary.Cell index={8} />
              </Table.Summary.Row>
            ) : null
          }
        />
      </FinanceSection>
    </div>
  );
}

function FinanceTextRow({ primary, secondary }) {
  return (
    <div className="fin-cell-text">
      <span className="fin-cell__primary">{primary}</span>
      {secondary ? <span className="fin-cell__secondary">{secondary}</span> : null}
    </div>
  );
}
